import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/system';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ConstructionIcon from '@mui/icons-material/Construction';
import StarIcon from '@mui/icons-material/Star';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import 'react-image-gallery/styles/css/image-gallery.css';
import { Theme } from '@mui/material/styles';
import { useGetProjectByIdQuery, useDeleteLegalDocumentMutation } from './api/projectsApi';
import LocationMap from './components/LocationMap';
import MapWithMarker from './components/LocationMap';
import AddLegalDocumentDialog from './components/AddLegalDocument';

interface LegalDocument {
  id: string;
  title: string;
  short_description?: string;
  file_path: string;
}

interface GalleryImage {
  original: string;
  thumbnail: string;
}

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 'bold',
  color: theme.palette.primary.main,
}));

const ProjectDetailPage = () => {
  const { id } = useParams();
  const { data, error, isLoading, refetch } = useGetProjectByIdQuery(id!);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<String | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDocumentMutation] = useDeleteLegalDocumentMutation();

  const handleDeleteClick = (docId: string) => {
    setDocumentToDelete(docId);
    setOpenConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    if (documentToDelete) {
      deleteDocumentMutation(documentToDelete);
    }
    setOpenConfirmModal(false);
    setDocumentToDelete(null);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Alert severity="error">Error loading project details</Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box mt={4}>
        <Alert severity="warning">Project not found</Alert>
      </Box>
    );
  }

  const project = data.data;

  // Prepare images for gallery
  const galleryImages: GalleryImage[] = [];
  if (project.project_image) {
    galleryImages.push({
      original: project.project_image,
      thumbnail: project.project_image,
    });
  }
  project.images.forEach((img: any) => {
    galleryImages.push({
      original: img.url,
      thumbnail: img.url,
    });
  });

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 4 } }}>
      {/* Project Header */}
      <Box mb={4}>
        <Typography variant="h3" component="h1" gutterBottom>
          {project.title}
          {project.featured === 1 && (
            <Chip
              icon={<StarIcon />}
              label="Featured"
              color="secondary"
              size="small"
              sx={{ ml: 2, verticalAlign: 'middle' }}
            />
          )}
        </Typography>

        <Box display="flex" alignItems="center" mb={2}>
          <ConstructionIcon color="action" sx={{ mr: 1 }} />
          <Typography variant="subtitle1" color="textSecondary">
            {project.construction_status}
          </Typography>
          {project.progress > 0 && (
            <>
              <Box width="100px" ml={2} mr={1}>
                <Box
                  sx={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: 'grey.200',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${project.progress}%`,
                      height: '100%',
                      backgroundColor: 'primary.main',
                    }}
                  />
                </Box>
              </Box>
              <Typography variant="body2" color="textSecondary">
                {project.progress}% Complete
              </Typography>
            </>
          )}
        </Box>

        <Box display="flex" alignItems="center">
          <LocationOnIcon color="action" sx={{ mr: 1 }} />
          <Typography variant="body1" color="textSecondary">
            Latitude: {project.latitude}, Longitude: {project.longitude}
          </Typography>
        </Box>
      </Box>

      {/* Image Gallery */}
      {galleryImages.length > 0 && (
        <StyledCard>
          <CardContent>
            <SectionTitle variant="h5">Project Images</SectionTitle>
            <Carousel 
              showThumbs={galleryImages.length > 1} 
              showStatus={false}
              thumbWidth={50}
              renderThumbs={(children) => 
                children.map((item, index) => (
                  <img
                    key={index}
                    src={galleryImages[index].original}
                    alt={`Thumbnail ${index + 1}`}
                    style={{
                      height: '50px',
                      width: '50px',
                      objectFit: 'cover'
                    }}
                  />
                ))
              }
            >
              {galleryImages.map((img, index) => (
                <div key={index}>
                  <img
                    src={img.original}
                    alt={`Project image ${index + 1}`}
                    style={{ 
                      width: '100%',
                      height: 'auto',
                      maxHeight: '400px',
                      objectFit: 'contain'
                    }}
                  />
                </div>
              ))}
            </Carousel>
          </CardContent>
        </StyledCard>
      )}

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          {/* Project Description */}
          <StyledCard>
            <CardContent>
              <SectionTitle variant="h5">Description</SectionTitle>
              <Typography variant="body1" paragraph>
                {project.description}
              </Typography>
            </CardContent>
          </StyledCard>

          {/* Amenities */}
          {project?.amenities && project?.amenities.length > 0 && (
            <StyledCard>
              <CardContent>
                <SectionTitle variant="h5">Amenities</SectionTitle>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {project.amenities && project.amenities.map((amenity: any, index: any) => (
                    <Chip key={index} label={amenity} variant="outlined" />
                  ))}
                </Box>
              </CardContent>
            </StyledCard>
          )}

          {/* Unit Types */}
          {project.unit_types && project.unit_types.length > 0 && (
            <StyledCard>
              <CardContent>
                <SectionTitle variant="h5">Unit Types</SectionTitle>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {project.unit_types.map((unitType: any) => (
                    <Chip
                      key={unitType.id}
                      label={unitType.name}
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              </CardContent>
            </StyledCard>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          {/* Project Details */}
          <StyledCard>
            <CardContent>
              <SectionTitle variant="h5">Project Details</SectionTitle>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Status"
                    secondary={project.construction_status}
                  />
                </ListItem>
                <Divider component="li" />
                {project.number_of_units && (
                  <>
                    <ListItem>
                      <ListItemText
                        primary="Number of Units"
                        secondary={project.number_of_units}
                      />
                    </ListItem>
                    <Divider component="li" />
                  </>
                )}
                {project.completion_year && (
                  <>
                    <ListItem>
                      <ListItemText
                        primary="Completion Year"
                        secondary={project.completion_year}
                      />
                    </ListItem>
                    <Divider component="li" />
                  </>
                )}
              </List>
            </CardContent>
          </StyledCard>

          <StyledCard sx={{ mt: 3 }}>
            <CardContent>
              <SectionTitle variant="h5">Location</SectionTitle>
              <Box mt={2}>
                <MapWithMarker lat={project.latitude} lng={project.longitude} />
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Legal Documents Section */}
      <Box mt={4}>
        <SectionTitle variant="h5">Legal Documents</SectionTitle>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          sx={{ mb: 2 }}
        >
          Add Document
        </Button>
        {(!project.legal_documents || project.legal_documents.length === 0) ? (
          <StyledCard>
            <CardContent>No legal documents available</CardContent>
          </StyledCard>
        ) : (
          <StyledCard>
            <CardContent>
              <List>
                {project.legal_documents.map((doc: LegalDocument) => (
                  <React.Fragment key={doc.id}>
                    <ListItem>
                      <DescriptionIcon color="primary" sx={{ mr: 2 }} />
                      <ListItemText
                        primary={doc.title}
                        secondary={doc.short_description || 'No description available'}
                      />
                      <MuiLink
                        href={doc.file_path}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        sx={{ mr: 2 }}
                      >
                        <Button variant="contained">
                          Download
                        </Button>
                      </MuiLink>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteClick(doc.id)}
                      >
                        Delete
                      </Button>
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </StyledCard>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
      >
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this document?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmModal(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <AddLegalDocumentDialog
        open={addDialogOpen}
        projectId={project.id}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={() => {
          refetch();
        }}
      />

      {/* Units Table */}
      {project.units && project.units.length > 0 && (
        <Box mt={4}>
          <SectionTitle variant="h5">Available Units</SectionTitle>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Unit Code</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Size (sqm)</TableCell>
                  <TableCell>Bedrooms</TableCell>
                  <TableCell>Bathrooms</TableCell>
                  <TableCell>Price Range</TableCell>
                  <TableCell>Availability</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {project.units.map((unit: any) => (
                  <TableRow key={unit.id}>
                    <TableCell>{unit.unit_code}</TableCell>
                    <TableCell>
                      {project.unit_types.find((t: any) => t.id === unit.unit_type_id)?.name || 'N/A'}
                    </TableCell>
                    <TableCell>{unit.size_sqm}</TableCell>
                    <TableCell>{unit.bedrooms}</TableCell>
                    <TableCell>{unit.bathrooms}</TableCell>
                    <TableCell>
                      {unit.price_min} - {unit.price_max}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={unit.availability}
                        color={unit.availability === 'Available' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      
    </Box>
  );
};

export default ProjectDetailPage;